import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchLoginMethods } from '../../api/profileExtras';
import { browserLoginMethods } from '../BrowserLogin/browserLoginRegistry';
import { LinkLoginContext } from '../BrowserLogin/LinkLoginContext';
import '../BrowserLogin/BrowserLoginPage.css';

const PROVIDER_LABELS = {
  telegram: 'Telegram',
  vk: 'VK ID',
};

export default function LinkAccountPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const providerFilter = (searchParams.get('provider') || '').trim().toLowerCase();
  const { token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [canAddTelegram, setCanAddTelegram] = useState(false);
  const [canAddVk, setCanAddVk] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        if (!cancelled) {
          setError('Нужна авторизация');
          setLoading(false);
        }
        return;
      }
      try {
        const data = await fetchLoginMethods(token);
        if (cancelled) return;
        setCanAddTelegram(Boolean(data.can_add_telegram));
        setCanAddVk(Boolean(data.can_add_vk));
      } catch {
        if (!cancelled) setError('Не удалось загрузить способы входа');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSuccess = useCallback(async () => {
    await refreshUser();
    navigate('/main/profile', { replace: true });
  }, [navigate, refreshUser]);

  const methodsToShow = useMemo(() => {
    const allowed = new Set();
    if (canAddTelegram) allowed.add('telegram');
    if (canAddVk) allowed.add('vk');
    if (providerFilter && allowed.has(providerFilter)) {
      return browserLoginMethods.filter((m) => m.id === providerFilter);
    }
    return browserLoginMethods.filter((m) => allowed.has(m.id));
  }, [canAddTelegram, canAddVk, providerFilter]);

  const linkContextValue = useMemo(
    () => ({
      linkMode: true,
      linkToken: token,
      onSuccess,
    }),
    [token, onSuccess]
  );

  if (loading) {
    return (
      <div className="browser-login-page">
        <div className="browser-login-page__shell">
          <p className="browser-login-page__subtitle">Загрузка…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="browser-login-page">
        <div className="browser-login-page__shell">
          <p className="browser-login-card__error">{error}</p>
          <button type="button" className="browser-login-card__desktop-btn" onClick={() => navigate(-1)}>
            Назад
          </button>
        </div>
      </div>
    );
  }

  if (methodsToShow.length === 0) {
    return (
      <div className="browser-login-page">
        <div className="browser-login-page__shell">
          <header className="browser-login-page__hero">
            <h1 className="browser-login-page__title">Привязка аккаунта</h1>
            <p className="browser-login-page__subtitle">Все доступные способы входа уже привязаны.</p>
          </header>
          <button
            type="button"
            className="browser-login-card__desktop-btn browser-login-card__desktop-btn--secondary"
            onClick={() => navigate('/main/profile', { replace: true })}
          >
            В профиль
          </button>
        </div>
      </div>
    );
  }

  const title =
    methodsToShow.length === 1
      ? `Привязать ${PROVIDER_LABELS[methodsToShow[0].id] || methodsToShow[0].id}`
      : 'Добавить способ входа';

  return (
    <LinkLoginContext.Provider value={linkContextValue}>
      <div className="browser-login-page">
        <div className="browser-login-page__shell">
          <header className="browser-login-page__hero">
            <button
              type="button"
              className="browser-login-card__desktop-btn browser-login-card__desktop-btn--secondary"
              style={{ marginBottom: 12, alignSelf: 'flex-start' }}
              onClick={() => navigate('/main/profile')}
            >
              ← Профиль
            </button>
            <p className="browser-login-page__brand">MatchWear</p>
            <h1 className="browser-login-page__title">{title}</h1>
            <p className="browser-login-page__subtitle">
              Войдите через выбранную платформу — аккаунт будет привязан к текущему профилю.
            </p>
          </header>

          <section className="browser-login-page__methods" aria-label="Привязка способа входа">
            {methodsToShow.map(({ id, Component }) => (
              <Component key={id} />
            ))}
          </section>

          <footer className="browser-login-page__footer">
            <p className="browser-login-page__footer-note">
              Продолжая, вы соглашаетесь с{' '}
              <Link to="/public-offer" className="browser-login-page__legal-link">
                условиями сервиса
              </Link>{' '}
              и{' '}
              <Link to="/policy" className="browser-login-page__legal-link">
                политикой конфиденциальности
              </Link>
              .
            </p>
          </footer>
        </div>
      </div>
    </LinkLoginContext.Provider>
  );
}
